import { cn, Skeleton, Tag } from '@noorden-insurtech/alba-design-system';
import type { PolicyBatchPoolSummary } from '@/services/integrations';
import { DonutChart } from '@/shared/ui';
import {
  buildPoolKey,
  buildPoolSegments,
  formatPoolTitle,
  isPoolExhausted,
  isPoolLow,
} from '../utils/policy-batch-summary.util';

type PoolRef = Pick<PolicyBatchPoolSummary, 'lobId' | 'channelCode'>;

interface PolicyBatchSummaryCardsProps {
  pools: PolicyBatchPoolSummary[];
  /** Mínimo configurado no BFF — card fica em alerta com `available <= minAvailable`. */
  minAvailable: number;
  loading?: boolean;
  /** Filtros ativos da tabela (strings, como no `useFilters`) para destacar o card selecionado. */
  activeLobId?: string;
  activeChannelCode?: string;
  onSelectPool: (_pool: PoolRef) => void;
}

const SKELETON_COUNT = 3;

const GRID_CLASS =
  'grid w-full grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3 lg:gap-4';

/**
 * Cards de saldo do balde, um por pool (ramo + origem de emissão), com donut
 * Disponível / Reservado / Utilizado. Componente burro: recebe os pools prontos
 * e devolve o clique — o filtro da tabela é decidido pela página.
 */
export function PolicyBatchSummaryCards({
  pools,
  minAvailable,
  loading = false,
  activeLobId,
  activeChannelCode,
  onSelectPool,
}: PolicyBatchSummaryCardsProps) {
  if (loading) {
    return (
      <div className={GRID_CLASS} aria-busy='true'>
        {Array.from({ length: SKELETON_COUNT }, (_, index) => (
          <Skeleton key={index} height={128} className='rounded-2xl' />
        ))}
      </div>
    );
  }

  if (!pools.length) return null;

  return (
    <div className={GRID_CLASS}>
      {pools.map(pool => {
        const isActive =
          activeLobId === String(pool.lobId) &&
          activeChannelCode === String(pool.channelCode);
        const low = isPoolLow(pool, minAvailable);
        const exhausted = isPoolExhausted(pool);
        const segments = buildPoolSegments(pool);
        const title = formatPoolTitle(pool);

        return (
          <button
            key={buildPoolKey(pool)}
            type='button'
            onClick={() => onSelectPool(pool)}
            aria-pressed={isActive}
            className={cn(
              'flex cursor-pointer items-center gap-4 rounded-2xl border bg-white px-3 py-3 text-left transition-all lg:px-4',
              low && 'border-2 border-alba-error-red-500',
              !low && isActive && 'border-2 border-alba-green-600 shadow-sm',
              !low &&
                !isActive &&
                'border-alba-gray-200 hover:border-alba-gray-300 hover:shadow-sm'
            )}
          >
            <DonutChart
              segments={segments}
              ariaLabel={`${title}: ${pool.available} disponíveis de ${pool.total}`}
              center={
                <span className='font-montserrat text-lg font-bold leading-none text-alba-black-500 tabular-nums'>
                  {pool.total}
                </span>
              }
            />

            <div className='flex min-w-0 flex-1 flex-col gap-1'>
              <div className='flex items-start justify-between gap-2'>
                <div className='min-w-0'>
                  <p className='truncate font-montserrat text-sm font-semibold text-alba-black-500'>
                    {title}
                  </p>
                  <p className='font-montserrat text-xs text-alba-gray-600'>
                    Origem de emissão {pool.channelCode}
                  </p>
                </div>
                {low && (
                  <Tag.Root variant='outline' colorScheme='error' size='sm'>
                    <Tag.Label>
                      {exhausted ? 'Esgotado' : 'Abaixo do mínimo'}
                    </Tag.Label>
                  </Tag.Root>
                )}
              </div>

              <div className='flex items-baseline gap-2'>
                <span
                  className={cn(
                    'font-montserrat text-2xl font-bold leading-none tabular-nums',
                    low ? 'text-alba-error-red-600' : 'text-alba-green-600'
                  )}
                >
                  {pool.available}
                </span>
                <span className='font-montserrat text-xs text-alba-gray-600'>
                  disponíveis de {pool.total}
                </span>
              </div>

              <ul className='flex flex-wrap gap-x-3 gap-y-1'>
                {segments.map(segment => (
                  <li
                    key={segment.key}
                    className='flex items-center gap-1 font-montserrat text-xs text-alba-gray-600'
                  >
                    <span
                      aria-hidden='true'
                      className='inline-block h-2 w-2 rounded-full'
                      style={{ backgroundColor: segment.color }}
                    />
                    {segment.label}
                    <span className='font-semibold text-alba-gray-700 tabular-nums'>
                      {segment.value}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </button>
        );
      })}
    </div>
  );
}
